/**
 * Guided-tour registry for the help agent.
 *
 * Tour ids here are the SINGLE source of truth — the backend's
 * `backend/web/src/help-agent/knowledge/tours.json` references these ids
 * in `start_tour` directives and must stay in sync when a tour is added.
 * Steps anchor to DOM elements carrying a matching `data-tour-id`
 * attribute (see TourOverlay/useTargetRect).
 */

export interface TourStep {
  /** Matches a `[data-tour-id="…"]` element. */
  targetId: string
  /** i18n keys in the `help_agent` namespace. Must start with `tour.` —
   *  enforced by the tourRegistry coverage test, NOT a template-literal
   *  type: a `tour.${string}` key type breaks the repo's typed t()
   *  resolution at the TourTooltip call site. */
  titleKey: string
  bodyKey: string
  /** Navigate here before locating the target (omit when already in place). */
  route?: string
  /** Preferred tooltip side; auto-flips when it would overflow the viewport. */
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** Px of breathing room around the spotlit target rect (default 8). */
  spotlightPadding?: number
}

export interface TourDefinition {
  id: string
  steps: TourStep[]
}

export const TOURS: Record<string, TourDefinition> = {
  'upload-data': {
    id: 'upload-data',
    steps: [
      {
        targetId: 'nav-data-catalog',
        route: '/data-catalog',
        titleKey: 'tour.upload_data.step_nav_title',
        bodyKey: 'tour.upload_data.step_nav_body',
        placement: 'right',
      },
      {
        targetId: 'data-catalog-upload',
        route: '/data-catalog',
        titleKey: 'tour.upload_data.step_upload_title',
        bodyKey: 'tour.upload_data.step_upload_body',
        placement: 'bottom',
      },
    ],
  },
  // Whole-app orientation: walks the four sidebar areas. First step pins
  // the route so the sidebar is guaranteed on screen; the rest stay put.
  'app-overview': {
    id: 'app-overview',
    steps: [
      {
        targetId: 'nav-intelligent-board',
        route: '/intelligent-board',
        titleKey: 'tour.app_overview.step_boards_title',
        bodyKey: 'tour.app_overview.step_boards_body',
        placement: 'right',
      },
      {
        targetId: 'nav-data-catalog',
        titleKey: 'tour.app_overview.step_data_title',
        bodyKey: 'tour.app_overview.step_data_body',
        placement: 'right',
      },
      {
        targetId: 'nav-digital-coworker',
        titleKey: 'tour.app_overview.step_dc_title',
        bodyKey: 'tour.app_overview.step_dc_body',
        placement: 'right',
      },
      {
        targetId: 'nav-workflow',
        titleKey: 'tour.app_overview.step_workflow_title',
        bodyKey: 'tour.app_overview.step_workflow_body',
        placement: 'right',
      },
    ],
  },
  'intelligent-boards': {
    id: 'intelligent-boards',
    steps: [
      {
        targetId: 'nav-intelligent-board',
        route: '/intelligent-board',
        titleKey: 'tour.intelligent_boards.step_nav_title',
        bodyKey: 'tour.intelligent_boards.step_nav_body',
        placement: 'right',
      },
      {
        targetId: 'board-create',
        route: '/intelligent-board',
        titleKey: 'tour.intelligent_boards.step_create_title',
        bodyKey: 'tour.intelligent_boards.step_create_body',
        placement: 'bottom',
      },
    ],
  },
  'create-workflow': {
    id: 'create-workflow',
    steps: [
      {
        targetId: 'nav-workflow',
        route: '/workflow',
        titleKey: 'tour.create_workflow.step_nav_title',
        bodyKey: 'tour.create_workflow.step_nav_body',
        placement: 'right',
      },
      {
        targetId: 'workflow-create',
        route: '/workflow',
        titleKey: 'tour.create_workflow.step_create_title',
        bodyKey: 'tour.create_workflow.step_create_body',
        placement: 'bottom',
      },
    ],
  },
  'digital-coworker': {
    id: 'digital-coworker',
    steps: [
      {
        targetId: 'nav-digital-coworker',
        route: '/digital-coworker',
        titleKey: 'tour.digital_coworker.step_nav_title',
        bodyKey: 'tour.digital_coworker.step_nav_body',
        placement: 'right',
      },
      {
        targetId: 'dc-create',
        route: '/digital-coworker',
        titleKey: 'tour.digital_coworker.step_create_title',
        bodyKey: 'tour.digital_coworker.step_create_body',
        placement: 'bottom',
      },
    ],
  },
}

export function getTour(tourId: string): TourDefinition | undefined {
  return TOURS[tourId]
}
